/**
 * Trade form — multi-item, Outgoing + Incoming arrays.
 *
 * On initial save: each Outgoing item decrements its source lot (sealed/card),
 * each Incoming item becomes a brand-new card or sealed lot tagged with the
 * trade's ID. The Pokémon ⌕ lookup button on incoming Card rows feeds the
 * existing quickCardLookup picker via a window._lookupOnPick override.
 */
import { DB } from "../lib/db";
import { CATEGORY_TO_GAME } from "../lib/catalogResolve";
import { exportTradeProposal } from "../components/printReport";
import { icon } from "../utils/icon";
import { escapeHtml, toast, scrollItemsToBottom, $ } from "../lib/dom";
import { requestEnrichment } from "../lib/enrich";
import { fmt$, fmtN, fmtDate, todayISO } from "../utils/format";
import { GAMES } from "../utils/pills";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { wireCombobox } from "../components/combobox";
import { wireLotSearch, type LotOption } from "../components/lotSearch";
import { mileageSectionHtml, wireMileageSection, saveMileageSection } from "../components/mileageSection";
import { normalizeSetName } from "../lib/setNames";
import { SetCache } from "../lib/sets";
import { quickCardLookup } from "../components/quickCardLookup";
import { openSealedPicker } from "../components/sealedProductPicker";
import { nextId } from "../utils/ids";
import { render } from "../state";
import { BULK_CLASSES, bulkPoolLotId, bulkUnitValueFor, addToBulkPool, type BulkClass } from "../lib/bulk";
import { holdingPeriod } from "../lib/taxation";
import { reverseEffect, effectForTransaction, type InvEffect } from "../lib/inventoryEffects";
import { cardLockReason } from "../lib/lotLock";
import { endEbayListingsForLots } from "../lib/ebayDelist";
import { looksGraded } from "../lib/slab";
import "../lib/collectrImport";

interface OutgoingItem {
  "Lot ID"?: string; "Item": string; "Type": string;
  "Qty": number; "Basis": number; "FMV": number;
  "BulkClass"?: BulkClass; // when Type === "Bulk"
  /** Captured from the source lot's Acq Date at trade time so holding period
   *  stays correct even if the source lot is later edited or deleted. */
  "Acq Date"?: string;
  /** Holding days at trade time (disposition date - Acq Date). null = Unknown. */
  "Holding Days"?: number | null;
  /** "Short-term" | "Long-term" | "Unknown" - drives tax-rate classification. */
  "Tax Treatment"?: string;
  /** Realized gain per item = (FMV - Basis) x Qty. Stored at trade time. */
  "Realized Gain"?: number;
}
interface IncomingItem {
  "AsType": "Card" | "Sealed" | "Bulk"; "Game": string;
  "Item": string; "Set": string; "Number": string;
  "Condition": string; "ProductType": string;
  "Image": string; "Qty": number; "FMV": number;
  "Lot ID"?: string;
  "BulkClass"?: BulkClass; // when AsType === "Bulk"
  "CardKind"?: "Raw" | "Graded"; // Card incoming only — Graded => Slab lot
}

/** A Condition string that looks like a third-party grade (PSA 10, BGS 9.5…). */
function isGradeValue(cond: string | undefined): boolean {
  const s = String(cond || "").trim();
  return /^(PSA|BGS|CGC|SGC)\b/i.test(s) || s.toLowerCase() === "pending";
}
interface TradeState {
  "Trade ID": string; "Date": string; "Notes": string; "Cash": number; "Status": string;
  "Outgoing": OutgoingItem[]; "Incoming": IncomingItem[];
  "_isNew": boolean;
}
function S(): TradeState | null { return (window as any)._tradeState as TradeState | null; }

export async function editTrade(id?: string, opts?: { seedStatus?: string }): Promise<void> {
  const isNew = !id;
  // Pull the lot-bearing tables fresh from cloud so the lot search reflects
  // the current qty/FMV (the local mirror can lag behind cloud writes made by
  // other tabs / devices, or by writes that hadn't fully settled). On edit we
  // also refresh trades so we hydrate from the latest stored row.
  const refreshes: Promise<void>[] = [DB.refreshTable("sealed"), DB.refreshTable("cards")];
  if (!isNew) refreshes.push(DB.refreshTable("trades"));
  await Promise.all(refreshes);
  const seedStatus = opts?.seedStatus || "Accepted";
  const trade = isNew
    ? { "Trade ID": nextId("TR", "trades", "Trade ID"), "Date": todayISO(),
        "Outgoing": [], "Incoming": [], "Notes": "", "Status": seedStatus }
    : DB.table("trades").find(r => r["Trade ID"] === id);
  if (!trade) { toast("Trade not found", "err"); return; }

  (window as any)._tradeState = {
    "Trade ID": trade["Trade ID"],
    "Date":     trade["Date"],
    "Notes":    trade["Notes"] || "",
    "Cash":     Number(trade["Cash"]) || 0,
    "Outgoing": (trade.Outgoing || []).map((x: any) => ({ ...x })),
    "Incoming": (trade.Incoming || []).map((x: any) => ({ ...x, CardKind: x.CardKind || (isGradeValue(x.Condition) ? "Graded" : "Raw") })),
    "Status":   String(trade["Status"] || "Accepted"),
    "_isNew":   isNew,
  } as TradeState;

  const body = `
    <form id="tradeForm" onsubmit="return false;">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        ${formField({ label: "Trade ID", name: "Trade ID", value: trade["Trade ID"], required: true })}
        ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(trade["Date"]), required: true })}
        ${formField({ label: "Status", name: "Status", value: String(trade["Status"] || "Accepted"), options: [{ value: "Proposed", label: "Pending" }, { value: "Accepted", label: "Completed" }, { value: "Declined", label: "Declined" }, { value: "Cancelled", label: "Cancelled" }], required: true, hint: "Pending / Declined / Cancelled don't touch inventory or books. Flipping to Completed applies the trade." })}
      </div>

      <div class="mb-6">
        <div class="mb-3 pb-2 border-b border-[var(--border)]">
          <h4 class="font-semibold text-cyan-300">Outgoing (items leaving inventory)</h4>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">Type below to add an existing lot; press Enter without selecting to add a free-text item. <b>+ Bulk</b> decrements one of the three bulk pools by quantity only.${!isNew ? " <span class='text-amber-300'>Editing won\'t re-apply inventory changes.</span>" : ""}</div>
        <div id="tradeOutgoing" class="space-y-2 max-h-[40vh] overflow-y-auto pr-1"></div>
        <div class="mt-3 flex items-stretch gap-2">
          <div class="combobox-wrapper flex-1 min-w-0" data-out-add-search>
            <input class="input w-full" autocomplete="off" placeholder="Type to add an outgoing lot…">
            <div class="combobox-list"></div>
          </div>
          <button type="button" class="btn btn-ghost !py-1 !px-3 text-xs whitespace-nowrap" onclick="addTradeOutgoingBulk()">+ Bulk</button>
        </div>
      </div>

      <div class="mb-6">
        <div class="flex items-center justify-between mb-3 pb-2 border-b border-[var(--border)] flex-wrap gap-2">
          <h4 class="font-semibold text-emerald-300">Incoming (new lots created on save)</h4>
          <div class="flex gap-2 flex-wrap">
            <button type="button" class="btn btn-ghost !py-1 !px-3 text-xs" onclick="triggerCollectrImport('trade-incoming')" title="Import incoming items from a Collectr portfolio CSV export. Items appear in the list below for review - nothing saves until you click Save.">${icon("upload")} Import CSV</button>
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addTradeIncoming('Card')">+ Card</button>
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addTradeIncoming('Sealed')">+ Sealed</button>
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addTradeIncomingBulk()">+ Bulk</button>
          </div>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">For Pokémon cards, click the ⌕ button on a row to find the card by name + number on the TCG API (image, set, FMV auto-fill). <b>+ Bulk</b> adds cards directly into one of the three bulk pools.</div>
        <div id="tradeIncoming" class="space-y-2 max-h-[40vh] overflow-y-auto pr-1"></div>
      </div>

      <div class="mb-3">
        <label class="label">Cash boot</label>
        <div class="flex flex-wrap items-center gap-2">
          <div class="inline-flex rounded border border-[var(--border)] overflow-hidden text-xs" id="tradeCashDir" data-dir="none">
            <button type="button" data-dir="received" class="cash-dir-btn px-3 py-1.5 text-[var(--muted)] hover:text-white" onclick="setTradeCashDirection('received')">I received</button>
            <button type="button" data-dir="paid"     class="cash-dir-btn px-3 py-1.5 border-l border-[var(--border)] text-[var(--muted)] hover:text-white" onclick="setTradeCashDirection('paid')">I paid</button>
            <button type="button" data-dir="none"     class="cash-dir-btn px-3 py-1.5 border-l border-[var(--border)] text-[var(--muted)] hover:text-white" onclick="setTradeCashDirection('none')">None</button>
          </div>
          <input class="input sm:max-w-xs" id="tradeCashAmount" type="number" step="0.01" min="0" placeholder="0.00" oninput="updateTradeCashAmount(this.value)">
          <input type="hidden" name="Cash" id="tradeCashHidden" value="${Number(trade["Cash"]) || 0}">
        </div>
        <div class="text-xs text-[var(--muted)] mt-1">Cash you pay adds to the incoming card(s)' basis; cash you receive counts as proceeds (realized gain). Use <b>None</b> for a straight swap with no boot.</div>
      </div>
      <div>${formField({ label: "Notes", name: "Notes", type: "textarea", value: trade["Notes"] || "" })}</div>

      ${mileageSectionHtml(trade["Trade ID"], "Trade")}

      <div class="mt-4 pt-3 pb-3 -mx-4 px-4 border-t border-[var(--border)] grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm sticky bottom-[68px] z-10 bg-[var(--panel)] shadow-[0_-8px_12px_-12px_rgba(0,0,0,0.6)]">
        <div><div class="text-xs text-[var(--muted)]">Outgoing Basis</div><div id="tradeOutBasis" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Outgoing FMV</div><div id="tradeOutFMV" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Incoming FMV</div><div id="tradeInFMV" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Cash</div><div id="tradeCashDisp" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Net G/L</div><div id="tradeGL" class="stat-num font-bold">$0.00</div></div>
      </div>
    </form>`;
  const safeId = escapeHtml(trade["Trade ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('trades','Trade ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="exportTradeProposalFromForm()" title="Open a printable proposal from the current form state (Save to PDF or print).">${icon("printer")} Print</button>
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-ghost" onclick="saveTradeAsDraft('${isNew ? "" : safeId}')" title="Save as a Pending draft - inventory is NOT touched. You can finish it later from the Trades page.">Save Draft</button>
    <span id="tradeStatusChip" class="pill pill-blue text-xs whitespace-nowrap" title="The Status you'\''re about to save with. Accepted = apply inventory + book realized gain.">Accepted</span>
    <button id="tradeSaveBtn" class="btn btn-primary" onclick="saveTrade('${isNew ? "" : safeId}')">${isNew ? "Log" : "Save"}</button>`;
  openModal({ title: isNew ? "Log Trade" : "Edit Trade", body, footer, wide: true });
  renderTradeOutgoingList();
  renderTradeIncomingList();
  initTradeCashUI();
  initTradeStatusChip();
  wireTradeOutgoingAddSearch();
  wireMileageSection();
}

/* ----- Outgoing ----- */
export function addTradeOutgoing(): void {
  const s = S(); if (!s) return;
  s.Outgoing.push({ "Lot ID": "", "Item": "", "Type": "", "Qty": 1, "Basis": 0, "FMV": 0 });
  renderTradeOutgoingList(); markModalDirty();
  scrollItemsToBottom("tradeOutgoing");
}
export function addTradeOutgoingBulk(): void {
  const s = S(); if (!s) return;
  const klass: BulkClass = "Bulk";
  const unit = bulkUnitValueFor(klass);
  s.Outgoing.push({
    "Lot ID": bulkPoolLotId(klass),
    "Item":   klass,
    "Type":   "Bulk",
    "BulkClass": klass,
    "Qty":    1,
    "Basis":  unit,
    "FMV":    unit,
  });
  renderTradeOutgoingList(); markModalDirty();
  scrollItemsToBottom("tradeOutgoing");
}
export function removeTradeOutgoing(idx: number): void {
  const s = S(); if (!s) return;
  s.Outgoing.splice(idx, 1);
  renderTradeOutgoingList(); markModalDirty();
}
export function updateTradeOutgoingField(idx: number, field: string, value: string): void {
  const s = S(); if (!s) return;
  const it = s.Outgoing[idx]; if (!it) return;
  (it as any)[field] = (field === "Qty" || field === "Basis" || field === "FMV") ? (Number(value) || 0) : value;
  recomputeTradeTotals(); markModalDirty();
}
/** Switch a bulk-outgoing row's class — re-syncs basis/FMV to the new Settings value. */
export function updateTradeOutgoingBulkClass(idx: number, klass: string): void {
  const s = S(); if (!s) return;
  const it = s.Outgoing[idx]; if (!it) return;
  if (klass !== "Bulk" && klass !== "Holo Bulk" && klass !== "Premium Bulk" && klass !== "$1-$5 Bulk" && klass !== "$5-$10 Bulk") return;
  const k = klass as BulkClass;
  const unit = bulkUnitValueFor(k);
  it["BulkClass"] = k;
  it["Item"]   = k;
  it["Lot ID"] = bulkPoolLotId(k);
  it["Basis"]  = unit;
  it["FMV"]    = unit;
  renderTradeOutgoingList(); markModalDirty();
}
function renderTradeOutgoingList(): void {
  const container = document.getElementById("tradeOutgoing");
  if (!container) return;
  const s = S(); if (!s) return;
  const items = s.Outgoing;
  document.getElementById("tradeOutgoingAddMore")?.classList.toggle("hidden", !items.length);
  if (!items.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No outgoing items yet — type below to add a lot, or use + Bulk for bulk pulls.</div>`;
    recomputeTradeTotals();
    return;
  }
  container.innerHTML = items.map((item, i) => {
    if (item.Type === "Bulk") {
      const klass = (item.BulkClass || "Bulk") as BulkClass;
      const unit = bulkUnitValueFor(klass);
      const pool = DB.table("cards").find(r => r["Card Lot ID"] === bulkPoolLotId(klass));
      const onHand = Number(pool?.["Qty"]) || 0;
      const pillColor = klass === "Bulk" ? "gray" : (klass === "Holo Bulk" ? "violet" : "amber");
      return `
        <div class="panel p-2 grid grid-cols-12 gap-2 items-end" data-out-idx="${i}">
          <div class="col-span-12 sm:col-span-5 flex items-center gap-2 flex-wrap">
            <span class="pill pill-${pillColor} shrink-0">Bulk (outgoing)</span>
            <select class="select !py-1 text-sm w-auto" onchange="updateTradeOutgoingBulkClass(${i},this.value)">
              ${BULK_CLASSES.map(k => `<option value="${escapeHtml(k)}" ${klass === k ? "selected" : ""}>${escapeHtml(k)}</option>`).join("")}
            </select>
            <span class="text-xs text-[var(--muted)]">On hand: <b>${fmtN(onHand)}</b> @ <b>${fmt$(unit)}</b>/card</span>
          </div>
          <div class="col-span-4 sm:col-span-2">
            <label class="label text-xs">Qty out</label>
            <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateTradeOutgoingField(${i},'Qty',this.value)">
          </div>
          <div class="col-span-8 sm:col-span-3 text-xs text-[var(--muted)]">
            Line basis & FMV use the <b>${escapeHtml(klass)}</b> pool's per-card value from Configs → Defaults. Save will decrement the pool by Qty out.
          </div>
          <div class="col-span-12 sm:col-span-2 flex sm:justify-end items-end">
            <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removeTradeOutgoing(${i})" title="Remove this item">✕</button>
          </div>
        </div>`;
    }
    return `
      <div class="panel p-2 grid grid-cols-12 gap-2 items-end" data-out-idx="${i}">
        <div class="col-span-12 sm:col-span-5">
          <label class="label text-xs">Search inventory or type</label>
          <div class="combobox-wrapper" data-out-search="${i}">
            <input class="input" autocomplete="off" placeholder="Type card / product name or lot ID…"
                   value="${escapeHtml(item.Item || "")}"
                   oninput="window._tradeState.Outgoing[${i}].Item=this.value;markModalDirty()">
            <div class="combobox-list"></div>
          </div>
          <div class="text-xs mt-1">${item["Lot ID"]
            ? `<span class="text-[var(--muted)]">Lot:</span> <code>${escapeHtml(item["Lot ID"])}</code> · <span class="pill pill-${item.Type === "Card" ? "violet" : "blue"}">${escapeHtml(item.Type || "")}</span>`
            : `<span class='text-amber-300'>No lot picked — saved as free text only.</span>`}</div>
        </div>
        <div class="col-span-3 sm:col-span-1">
          <label class="label text-xs">Qty</label>
          <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateTradeOutgoingField(${i},'Qty',this.value)">
        </div>
        <div class="col-span-4 sm:col-span-2">
          <label class="label text-xs">Unit Basis</label>
          <input class="input" type="number" step="0.01" value="${item.Basis || 0}" oninput="updateTradeOutgoingField(${i},'Basis',this.value)">
        </div>
        <div class="col-span-4 sm:col-span-2">
          <label class="label text-xs">Unit FMV</label>
          <input class="input" type="number" step="0.01" value="${item.FMV || 0}" oninput="updateTradeOutgoingField(${i},'FMV',this.value)">
        </div>
        <div class="col-span-1 sm:col-span-2 flex sm:justify-end items-end">
          <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removeTradeOutgoing(${i})" title="Remove this item">✕</button>
        </div>
      </div>`;
  }).join("");
  // Per-row lot search; hide lots already picked in OTHER rows. Bulk rows have no search.
  items.forEach((item, i) => {
    if (item.Type === "Bulk") return;
    const wrapper = container.querySelector<HTMLElement>(`[data-out-search="${i}"]`);
    if (!wrapper) return;
    wireLotSearch(wrapper, {
      excludeLocked: true,
      excludeIds: () => {
        const set = new Set<string>();
        const cur = S();
        (cur?.Outgoing || []).forEach((x, j) => { if (j !== i && x["Lot ID"]) set.add(x["Lot ID"]!); });
        return set;
      },
      onSelect: (lot) => {
        const cur = S(); if (!cur) return;
        const it = cur.Outgoing[i];
        it["Lot ID"] = lot.id;
        // Strip the "— qty N" suffix the search adds, plus the leading ID prefix.
        it["Item"]   = (lot.label.split(" — ")[0] || lot.id).replace(/^[A-Z]+-\d+-\d+ · /, "");
        it["Type"]   = lot.type;
        it["Basis"]  = lot.basis;
        it["FMV"]    = lot.fmv;
        renderTradeOutgoingList(); markModalDirty();
      },
    });
  });
  recomputeTradeTotals();
}

/* ----- Incoming ----- */
export function addTradeIncoming(asType: "Card" | "Sealed"): void {
  const s = S(); if (!s) return;
  s.Incoming.push({
    "AsType": asType, "Game": "Pokemon",
    "Item": "", "Set": "", "Number": "", "Condition": "Near Mint", "ProductType": "",
    "Image": "", "Qty": 1, "FMV": 0,
  });
  renderTradeIncomingList(); markModalDirty();
  scrollItemsToBottom("tradeIncoming");
}
export function addTradeIncomingBulk(): void {
  const s = S(); if (!s) return;
  const klass: BulkClass = "Bulk";
  const unit = bulkUnitValueFor(klass);
  s.Incoming.push({
    "AsType": "Bulk", "Game": "Other",
    "Item": klass, "Set": "", "Number": "", "Condition": "", "ProductType": "",
    "Image": "", "Qty": 1, "FMV": unit,
    "BulkClass": klass,
  });
  renderTradeIncomingList(); markModalDirty();
  scrollItemsToBottom("tradeIncoming");
}
export function removeTradeIncoming(idx: number): void {
  const s = S(); if (!s) return;
  s.Incoming.splice(idx, 1);
  renderTradeIncomingList(); markModalDirty();
}
export function updateTradeIncomingField(idx: number, field: string, value: string): void {
  const s = S(); if (!s) return;
  const it = s.Incoming[idx]; if (!it) return;
  (it as any)[field] = (field === "Qty" || field === "FMV") ? (Number(value) || 0) : value;
  recomputeTradeTotals(); markModalDirty();
}
/** Switch a bulk-incoming row's class — re-syncs FMV to the new Settings value. */
export function updateTradeIncomingBulkClass(idx: number, klass: string): void {
  const s = S(); if (!s) return;
  const it = s.Incoming[idx]; if (!it) return;
  if (klass !== "Bulk" && klass !== "Holo Bulk" && klass !== "Premium Bulk" && klass !== "$1-$5 Bulk" && klass !== "$5-$10 Bulk") return;
  const k = klass as BulkClass;
  const unit = bulkUnitValueFor(k);
  it["BulkClass"] = k;
  it["Item"] = k;
  it["FMV"] = unit;
  renderTradeIncomingList(); markModalDirty();
}
function renderTradeIncomingList(): void {
  const container = document.getElementById("tradeIncoming");
  if (!container) return;
  const s = S(); if (!s) return;
  const items = s.Incoming;
  document.getElementById("tradeIncomingAddMore")?.classList.toggle("hidden", !items.length);
  if (!items.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No incoming items yet — click "+ Card", "+ Sealed", or "+ Bulk".</div>`;
    recomputeTradeTotals();
    return;
  }
  container.innerHTML = items.map((item, i) => {
    if (item.AsType === "Bulk") {
      const klass = (item.BulkClass || "Bulk") as BulkClass;
      const unit = bulkUnitValueFor(klass);
      const pillColor = klass === "Bulk" ? "gray" : (klass === "Holo Bulk" ? "violet" : "amber");
      return `
        <div class="panel p-2" data-in-idx="${i}">
          <div class="flex items-center gap-2 mb-2 flex-wrap">
            <span class="pill pill-${pillColor}">Bulk (incoming)</span>
            <select class="select !py-1 text-sm w-auto" onchange="updateTradeIncomingBulkClass(${i},this.value)">
              ${BULK_CLASSES.map(k => `<option value="${escapeHtml(k)}" ${klass === k ? "selected" : ""}>${escapeHtml(k)}</option>`).join("")}
            </select>
            <span class="text-xs text-[var(--muted)]">@ <b>${fmt$(unit)}</b>/card from Configs → Defaults</span>
            <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs ml-auto" onclick="removeTradeIncoming(${i})" title="Remove">✕</button>
          </div>
          <div class="grid grid-cols-12 gap-2">
            <div class="col-span-4 sm:col-span-2">
              <label class="label text-xs">Qty in</label>
              <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateTradeIncomingField(${i},'Qty',this.value)">
            </div>
            <div class="col-span-8 sm:col-span-10 text-xs text-[var(--muted)] self-end pb-1">
              On save, ${fmtN(Number(item.Qty) || 0)} cards bump the <b>${escapeHtml(klass)}</b> pool (qty only). Line FMV = ${fmt$((Number(item.Qty) || 0) * unit)}.
            </div>
          </div>
        </div>`;
    }
    const isCard = item.AsType === "Card";
    const typeBadge = isCard ? `<span class="pill pill-violet">Card</span>` : `<span class="pill pill-blue">Sealed</span>`;
    const lookupBtn = isCard
      ? `<button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="lookupForTradeIncoming(${i})" title="Find on Pokémon TCG API">⌕ Lookup</button>`
      : `<button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="lookupSealedForTradeIncoming(${i})" title="Find in the sealed product catalog">⌕ Lookup</button>`;
    return `
      <div class="panel p-2" data-in-idx="${i}">
        <div class="flex items-center gap-2 mb-2 flex-wrap">
          ${typeBadge}
          <select class="select !py-1 text-sm w-auto" onchange="updateTradeIncomingField(${i},'Game',this.value);(window.renderTradeIncomingList||(()=>{}))()">
            ${GAMES.map(g => `<option value="${escapeHtml(g)}" ${item.Game === g ? "selected" : ""}>${escapeHtml(g)}</option>`).join("")}
          </select>
          ${isCard ? `<select class="select !py-1 text-sm w-auto" onchange="updateTradeIncomingCardKind(${i}, this.value)" title="Raw or graded card">
            <option value="Raw" ${item.CardKind !== "Graded" ? "selected" : ""}>Raw</option>
            <option value="Graded" ${item.CardKind === "Graded" ? "selected" : ""}>Graded</option>
          </select>` : ""}
          ${lookupBtn}
          ${item.Image ? `<img src="${escapeHtml(item.Image)}" class="rounded ml-1" style="height:36px; width:auto;" alt="">` : ""}
          <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs ml-auto" onclick="removeTradeIncoming(${i})" title="Remove">✕</button>
        </div>
        <div class="grid grid-cols-12 gap-2">
          <div class="col-span-12 sm:col-span-5">
            <label class="label text-xs">${isCard ? "Card Name" : "Product Name"}</label>
            <input class="input" placeholder="${isCard ? 'e.g. Charizard ex' : 'e.g. Surging Sparks Booster Box'}"
                   value="${escapeHtml(item.Item || "")}"
                   oninput="updateTradeIncomingField(${i},'Item',this.value)">
          </div>
          <div class="col-span-8 sm:col-span-3">
            <label class="label text-xs">Set</label>
            <div class="combobox-wrapper" data-trade-in-set="${i}">
              <input class="input" autocomplete="off" placeholder="Type to search…"
                     value="${escapeHtml(item.Set || "")}"
                     oninput="updateTradeIncomingField(${i},'Set',this.value)">
              <div class="combobox-list"></div>
            </div>
          </div>
          <div class="col-span-4 sm:col-span-2">
            <label class="label text-xs">${isCard ? "Card #" : "Type"}</label>
            <input class="input" placeholder="${isCard ? '#' : 'e.g. ETB'}"
                   value="${escapeHtml(isCard ? (item.Number || "") : (item.ProductType || ""))}"
                   oninput="updateTradeIncomingField(${i},'${isCard ? 'Number' : 'ProductType'}',this.value)">
          </div>
          ${isCard ? `
          <div class="col-span-12 sm:col-span-2">
            <label class="label text-xs">${item.CardKind === "Graded" ? "Grade" : "Condition"}</label>
            <select class="select" onchange="updateTradeIncomingField(${i},'Condition',this.value)">
              ${(item.CardKind === "Graded"
                  ? ["PSA 10","PSA 9","PSA 8","BGS 10","BGS 9.5","BGS 9","CGC 10","CGC 9.5","SGC 10","Pending","Other"]
                  : ["Near Mint","Mint","Lightly Played","Moderately Played","Heavily Played","Damaged","Other"]
                ).map(o => `<option ${item.Condition === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
            </select>
          </div>` : `<div class="col-span-12 sm:col-span-2"></div>`}
          <div class="col-span-3 sm:col-span-1">
            <label class="label text-xs">Qty</label>
            <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updateTradeIncomingField(${i},'Qty',this.value)">
          </div>
          <div class="col-span-9 sm:col-span-2">
            <label class="label text-xs">Unit FMV</label>
            <input class="input" type="number" step="0.01" value="${item.FMV || 0}" oninput="updateTradeIncomingField(${i},'FMV',this.value)">
          </div>
        </div>
      </div>`;
  }).join("");
  // Per-row Set combobox follows the row's Game (bulk rows have no Set field)
  items.forEach((item, i) => {
    if (item.AsType === "Bulk") return;
    const wrapper = container.querySelector<HTMLElement>(`[data-trade-in-set="${i}"]`);
    if (!wrapper) return;
    const getGame = () => {
      const cur = S();
      return (cur?.Incoming[i]?.Game) || "Pokemon";
    };
    wireCombobox(wrapper, {
      getOptions: () => SetCache.get(getGame()),
      asyncLoader: () => SetCache.ensureFresh(getGame()),
      onSelect: (v) => updateTradeIncomingField(i, "Set", v),
    });
  });
  recomputeTradeTotals();
}

export function lookupForTradeIncoming(idx: number): void {
  const s = S(); if (!s) return;
  const item = s.Incoming[idx];
  if (!item || item.AsType !== "Card") return;
  // Wire the Pokémon picker to feed THIS incoming row instead of a (nonexistent) card form
  (window as any)._lookupOnPick = (c: any) => {
    item.Item   = c.name || "";
    item.Set    = normalizeSetName((c.set && c.set.name) || "");
    item.Number = c.number || "";
    item.Image  = (c.images && (c.images.small || c.images.large)) || "";
    item.Game   = c.game || item.Game || "Pokemon";
    const tcg = c.tcgplayer && c.tcgplayer.prices;
    if (tcg) {
      const p = Object.values<any>(tcg).find(x => x && typeof x.market === "number");
      if (p) item.FMV = +Number(p.market).toFixed(2);
    }
    renderTradeIncomingList(); markModalDirty();
    toast(`Picked: ${c.name}${c.set?.name ? " · " + c.set.name : ""}`);
    setTimeout(() => { delete (window as any)._lookupOnPick; }, 100);
  };
  const seed = (item.Item || "").trim() || "Pokemon";
  const narrow: { number?: string; set?: string; game?: string } = { game: item.Game || "Pokemon" };
  if (item.Number) narrow.number = item.Number.trim();
  if (item.Set)    narrow.set    = item.Set.trim();
  quickCardLookup(1, seed, narrow);
}

export function lookupSealedForTradeIncoming(idx: number): void {
  const s = S(); if (!s) return;
  const item = s.Incoming[idx];
  if (!item || item.AsType !== "Sealed") return;
  (window as any)._sealedOnPick = (r: any) => {
    item.Item = r.name || "";
    item.Set  = normalizeSetName(r.group_name || item.Set || "");
    if (CATEGORY_TO_GAME[r.category_id]) item.Game = CATEGORY_TO_GAME[r.category_id];
    const ptMap: Record<string, string> = {
      box: "Booster Box", etb: "ETB", bundle: "Booster Bundle",
      pack: "Booster Pack", tin: "Tin", blister: "Blister", deck: "Deck",
      collection: "Collection Box", other: "Other",
    };
    if (r.product_kind && ptMap[r.product_kind]) item.ProductType = ptMap[r.product_kind];
    renderTradeIncomingList(); markModalDirty();
    toast(`Picked: ${r.name}`);
    setTimeout(() => { delete (window as any)._sealedOnPick; }, 100);
  };
  openSealedPicker((item.Item || "").trim() || undefined);
}

function recomputeTradeTotals(): void {
  const s = S(); if (!s) return;
  const out = s.Outgoing || [];
  const inc = s.Incoming || [];
  const outBasis = out.reduce((acc, x) => acc + (Number(x.Basis) || 0) * (Number(x.Qty) || 1), 0);
  const outFMV   = out.reduce((acc, x) => acc + (Number(x.FMV)   || 0) * (Number(x.Qty) || 1), 0);
  const inFMV    = inc.reduce((acc, x) => acc + (Number(x.FMV)   || 0) * (Number(x.Qty) || 1), 0);
  const gl = +(inFMV - outFMV + (Number(s["Cash"]) || 0)).toFixed(2);
  const setText = (id: string, val: string) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  const cash = Number(s["Cash"]) || 0;
  setText("tradeOutBasis", fmt$(outBasis));
  setText("tradeOutFMV",   fmt$(outFMV));
  setText("tradeInFMV",    fmt$(inFMV));
  setText("tradeCashDisp", fmt$(cash));
  setText("tradeGL",       fmt$(gl));
  const glEl = document.getElementById("tradeGL");
  if (glEl) glEl.className = "stat-num font-bold " + (gl >= 0 ? "text-emerald-300" : "text-rose-300");
}

/* ----- Cash boot: direction toggle + absolute-value amount ----- */

function _cashDirEl(): HTMLElement | null { return document.getElementById("tradeCashDir"); }
function _cashAmtEl(): HTMLInputElement | null { return document.getElementById("tradeCashAmount") as HTMLInputElement | null; }
function _cashHiddenEl(): HTMLInputElement | null { return document.getElementById("tradeCashHidden") as HTMLInputElement | null; }

function _getCashDir(): "received" | "paid" | "none" {
  const w = _cashDirEl();
  const v = w?.getAttribute("data-dir");
  return v === "received" || v === "paid" ? v : "none";
}

function _setCashDir(dir: "received" | "paid" | "none"): void {
  const w = _cashDirEl(); if (!w) return;
  w.setAttribute("data-dir", dir);
  w.querySelectorAll<HTMLButtonElement>(".cash-dir-btn").forEach(b => {
    const isActive = b.getAttribute("data-dir") === dir;
    if (isActive) {
      b.classList.add("bg-[var(--panel-2)]", "!text-white");
      b.classList.remove("text-[var(--muted)]");
    } else {
      b.classList.remove("bg-[var(--panel-2)]", "!text-white");
      b.classList.add("text-[var(--muted)]");
    }
  });
  const amt = _cashAmtEl();
  if (amt) {
    amt.disabled = (dir === "none");
    if (dir === "none") amt.value = "";
  }
}

function _writeSignedCash(amount: number, dir: "received" | "paid" | "none"): void {
  const s = S(); if (!s) return;
  let cash = 0;
  if (dir === "received") cash = amount;
  else if (dir === "paid") cash = -amount;
  s.Cash = +cash.toFixed(2);
  const h = _cashHiddenEl();
  if (h) h.value = String(s.Cash);
}

/** Called from editTrade after the modal mounts. Derives initial UI state
 *  from the trade's stored Cash value (positive = received, negative = paid,
 *  zero = none). */
function initTradeCashUI(): void {
  const s = S(); if (!s) return;
  const dir: "received" | "paid" | "none" = s.Cash > 0 ? "received" : (s.Cash < 0 ? "paid" : "none");
  _setCashDir(dir);
  const amt = _cashAmtEl();
  if (amt) amt.value = dir === "none" ? "" : String(Math.abs(s.Cash));
  const h = _cashHiddenEl();
  if (h) h.value = String(s.Cash);
}

/** Direction toggle click handler. */
export function setTradeCashDirection(dir: string): void {
  const d: "received" | "paid" | "none" = dir === "received" || dir === "paid" ? dir : "none";
  _setCashDir(d);
  const amt = _cashAmtEl();
  const amount = Math.abs(Number(amt?.value) || 0);
  _writeSignedCash(amount, d);
  recomputeTradeTotals();
  markModalDirty();
}

/** Amount input handler. Picks "received" by default if no direction has been
 *  chosen yet (positive amount = money in, the more common boot). */
export function updateTradeCashAmount(v: string): void {
  const amount = Math.abs(Number(v) || 0);
  let dir = _getCashDir();
  if (dir === "none" && amount > 0) {
    dir = "received";
    _setCashDir(dir);
  }
  _writeSignedCash(amount, dir);
  recomputeTradeTotals();
  markModalDirty();
}

/** @deprecated kept for any stale inline handlers; new code uses
 *  setTradeCashDirection + updateTradeCashAmount. */
export function updateTradeCash(v: string): void {
  const s = S(); if (!s) return;
  s.Cash = Number(v) || 0;
  recomputeTradeTotals();
  markModalDirty();
}

/** Toggle an incoming card row between Raw and Graded — swaps the condition /
 *  grade options and resets to a sensible default for the new kind. */
export function updateTradeIncomingCardKind(idx: number, value: string): void {
  const s = S(); if (!s) return;
  const it = s.Incoming[idx]; if (!it) return;
  it.CardKind = value === "Graded" ? "Graded" : "Raw";
  it.Condition = it.CardKind === "Graded" ? "PSA 10" : "Near Mint";
  renderTradeIncomingList(); markModalDirty();
}

/** True when an edit changed inventory-affecting fields on either side of the
 *  trade (outgoing lot/qty/type, or any incoming item). */
function tradeInventoryChanged(existing: any, newOut: OutgoingItem[], newInc: IncomingItem[]): boolean {
  const outSig = (arr: any[]) => arr.map(x => `${x.Type || ""}|${x["Lot ID"] || ""}|${x.BulkClass || ""}|${Number(x.Qty) || 0}`).sort().join("~");
  const inSig  = (arr: any[]) => arr.map(x => `${x.AsType || ""}|${x.Item || ""}|${x.BulkClass || ""}|${x.Condition || ""}|${Number(x.Qty) || 0}|${Number(x.FMV) || 0}`).sort().join("~");
  const oldOut = Array.isArray(existing?.Outgoing) ? existing.Outgoing : [];
  const oldInc = Array.isArray(existing?.Incoming) ? existing.Incoming : [];
  return outSig(oldOut) !== outSig(newOut) || inSig(oldInc) !== inSig(newInc);
}

export function saveTrade(origId: string): void {
  const s = S(); if (!s) return;
  const formData = readForm($("#tradeForm"));
  const tradeId = (formData["Trade ID"] || s["Trade ID"] || nextId("TR", "trades", "Trade ID")).trim();
  let date = formData["Date"] || todayISO();
  const notes = formData["Notes"] || "";
  const status = String(formData["Status"] || "Accepted");
  const isAccepted = status === "Accepted";
  const cash = Number(formData["Cash"]) || 0;       // + received / − paid
  const cashPaid     = cash < 0 ? -cash : 0;

  const outgoing = (s.Outgoing || []).filter(x =>
    x.Type === "Bulk" ? (Number(x.Qty) || 0) > 0 : (x.Item || x["Lot ID"])
  );
  const incoming = (s.Incoming || []).filter(x =>
    x.AsType === "Bulk" ? (Number(x.Qty) || 0) > 0 : x.Item
  );
  if (!outgoing.length && !incoming.length) { toast("Add at least one outgoing or incoming item.", "err"); return; }

  // A card physically out for grading/repair cannot be traded until its
  // submission is marked Returned. (The database enforces this too.)
  for (const o of outgoing) {
    if (!o["Lot ID"]) continue;
    const lockReason = cardLockReason(o["Lot ID"]!);
    if (lockReason) { toast(`${o["Lot ID"]} is ${lockReason} \u2014 mark it Returned before trading it.`, "err"); return; }
  }

  const outBasis = outgoing.reduce((acc, x) => acc + (Number(x.Basis) || 0) * (Number(x.Qty) || 1), 0);
  const outFMV   = outgoing.reduce((acc, x) => acc + (Number(x.FMV)   || 0) * (Number(x.Qty) || 1), 0);
  const inFMV    = incoming.reduce((acc, x) => acc + (Number(x.FMV)   || 0) * (Number(x.Qty) || 1), 0);
  const isNew = !origId;
  const existing = isNew ? null : DB.table("trades").find(r => r["Trade ID"] === origId);
  const wasAccepted = !isNew && String(existing?.["Status"] || "Accepted") === "Accepted";

  // Once a trade is Accepted, we don't allow flipping it back to a proposal -
  // inventory + realized gain are already booked. Delete and re-create instead.
  if (wasAccepted && !isAccepted) {
    toast("Can't change a completed trade back to pending / declined / cancelled. Delete it and re-create as a draft.", "err");
    return;
  }

  // When a proposal is accepted, the disposition date is when the swap actually
  // happens. Auto-update Date to today unless the user already changed it
  // (i.e. the form date still equals the stored proposal date).
  if (!isNew && !wasAccepted && isAccepted) {
    const existingDate = String(existing?.["Date"] || "");
    if (date === existingDate) date = todayISO();
  }

  // Capture holding period + realized gain per outgoing item. A trade is a
  // taxable disposition of the outgoing items (collectibles are not eligible
  // for sec.1031 treatment post-2018), so we book each item's gain at trade time.
  // Free-text outgoing (no Lot ID) carry "Unknown" treatment since we can't
  // look up an Acq Date. Bulk outgoing uses trade date as Acq Date (pool is
  // fungible) -> always short-term.
  let totalRealizedGain = 0;
  if (isAccepted) {
    // Amount realized on the outgoing cards = the value received in exchange
    // (incoming FMV + signed cash boot), NOT the outgoing cards' own FMV.
    // Allocated across outgoing lots by FMV weight; each lot's gain = its share
    // of proceeds minus its basis.
    const proceeds = +(inFMV + cash).toFixed(2);
    for (const out of outgoing) {
      const qtyOut = Number(out.Qty) || 1;
      const lineFMV   = (Number(out.FMV) || 0) * qtyOut;
      const lineBasis = (Number(out.Basis) || 0) * qtyOut;
      const lotProceeds = outFMV > 0 ? (lineFMV / outFMV) * proceeds : proceeds / Math.max(outgoing.length, 1);
      const realized = +(lotProceeds - lineBasis).toFixed(2);
      out["Realized Gain"] = realized;
      totalRealizedGain += realized;

      let acq: string | undefined;
      if (out.Type === "Bulk") {
        acq = date;
      } else if (out["Lot ID"]) {
        const sealed = DB.table("sealed").find(r => r["Lot ID"] === out["Lot ID"]);
        const card   = DB.table("cards").find(r => r["Card Lot ID"] === out["Lot ID"]);
        acq = (sealed?.["Acq Date"] || card?.["Acq Date"]) as string | undefined;
      }
      out["Acq Date"] = acq || "";
      const hp = holdingPeriod(acq, date);
      out["Holding Days"]  = hp.days;
      out["Tax Treatment"] = hp.treatment;
    }
    totalRealizedGain = +totalRealizedGain.toFixed(2);
  } else {
    // Non-accepted: don't book realized gain or holding period on outgoing items.
    for (const out of outgoing) {
      delete (out as any)["Realized Gain"];
      delete (out as any)["Acq Date"];
      delete (out as any)["Holding Days"];
      delete (out as any)["Tax Treatment"];
    }
  }


  // Decide whether to (re)apply inventory effects. New trades always apply.
  // Edits only re-touch inventory when an inventory-affecting field changed —
  // and then only after the user confirms.
  // Only Accepted trades touch inventory. A status transition from non-
  // Accepted to Accepted triggers a fresh apply. An edit on an already-
  // Accepted trade re-applies only when inventory/cash actually changed.
  let applyInventory = false;
  if (isAccepted) {
    if (isNew || !wasAccepted) {
      applyInventory = true;
    } else {
      const cashChanged = (Number(existing?.["Cash"]) || 0) !== cash;
      if (cashChanged || tradeInventoryChanged(existing, outgoing, incoming)) {
        if (!confirm("This trade's items or cash changed. Reverse the original inventory changes (restore outgoing lots, delete the lots this trade created) and re-apply with the new values?")) return;
        const oldEffect = effectForTransaction("trades", existing);
        const res = reverseEffect(oldEffect, { excludeId: origId });
        if (!res.ok) { toast("Can't edit: " + res.blockedBy, "err"); return; }
        applyInventory = true;
      }
    }
  }

  // Bulk activity summary for the toast.
  let bulkOutCards = 0;
  let bulkInCards  = 0;
  const bulkPoolsTouched = new Set<string>();
  let effect: InvEffect = [];

  if (applyInventory) {
    // Decrement source lots for each outgoing item (recording exact deltas).
    for (const out of outgoing) {
      const qtyOut = Number(out.Qty) || 1;
      if (out.Type === "Bulk" && out.BulkClass) {
        addToBulkPool(out.BulkClass, -qtyOut);
        out["Lot ID"] = bulkPoolLotId(out.BulkClass);
        effect.push({ kind: "poolDelta", klass: out.BulkClass, dQty: -qtyOut });
        bulkOutCards += qtyOut;
        bulkPoolsTouched.add(out.BulkClass);
        continue;
      }
      if (!out["Lot ID"]) continue;
      const sealed = DB.table("sealed").find(r => r["Lot ID"] === out["Lot ID"]);
      const card   = DB.table("cards").find(r  => r["Card Lot ID"] === out["Lot ID"]);
      if (sealed) {
        const curQty = Number(sealed["Qty"]) || 0;
        const oldQty = Math.max(curQty, 1);
        const newQty = Math.max(0, curQty - qtyOut);
        const oldFMV = Number(sealed["Current FMV"]) || 0;
        const newFMV = +(oldFMV / oldQty * newQty).toFixed(2);
        const statusBefore = sealed["Status"];
        const patch: Record<string, any> = {
          "Qty": newQty,
          "Total Basis": +((Number(sealed["Unit Basis"]) || 0) * newQty).toFixed(2),
          "Current FMV": newFMV,
        };
        let statusAfter: string | undefined;
        if (newQty === 0) { patch["Status"] = "Traded"; statusAfter = "Traded"; }
        DB.update("sealed", "Lot ID", out["Lot ID"]!, patch);
        effect.push({ kind: "lotDelta", table: "sealed", lotId: out["Lot ID"]!, dQty: newQty - curQty, dFMV: newFMV - oldFMV, statusBefore, statusAfter });
      } else if (card) {
        const curQty = Number(card["Qty"]) || 0;
        const oldQty = Math.max(curQty, 1);
        const newQty = Math.max(0, curQty - qtyOut);
        const oldFMV = Number(card["Current FMV"]) || 0;
        const newFMV = +(oldFMV / oldQty * newQty).toFixed(2);
        const statusBefore = card["Status"];
        const patch: Record<string, any> = {
          "Qty": newQty,
          "Total Basis": +((Number(card["Unit Basis"]) || 0) * newQty).toFixed(2),
          "Current FMV": newFMV,
        };
        let statusAfter: string | undefined;
        if (newQty === 0) { patch["Status"] = "Traded"; statusAfter = "Traded"; }
        DB.update("cards", "Card Lot ID", out["Lot ID"]!, patch);
        effect.push({ kind: "lotDelta", table: "cards", lotId: out["Lot ID"]!, dQty: newQty - curQty, dFMV: newFMV - oldFMV, statusBefore, statusAfter });
      }
    }
    // Cash you paid capitalizes into the incoming card/sealed lots (FMV-weighted).
    const incNonBulk = incoming.filter(x => x.AsType !== "Bulk");
    const totIncFMV = incNonBulk.reduce((a, x) => a + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);

    // Create new lots for each incoming (recording each as a reversible create).
    for (const inc of incoming) {
      const qty = Number(inc.Qty) || 1;
      const unitFMV = Number(inc.FMV) || 0;
      if (inc.AsType === "Bulk" && inc.BulkClass) {
        addToBulkPool(inc.BulkClass, qty);
        inc["Lot ID"] = bulkPoolLotId(inc.BulkClass);
        effect.push({ kind: "poolDelta", klass: inc.BulkClass, dQty: qty });
        bulkInCards += qty;
        bulkPoolsTouched.add(inc.BulkClass);
        continue;
      }
      // Capitalize this lot's FMV-weighted share of any cash paid into its basis.
      const lineFMV = unitFMV * qty;
      const cashShare = cashPaid > 0
        ? (totIncFMV > 0 ? +((lineFMV / totIncFMV) * cashPaid).toFixed(2) : +(cashPaid / Math.max(incNonBulk.length, 1)).toFixed(2))
        : 0;
      const lotTotalBasis = +(lineFMV + cashShare).toFixed(2);
      const lotUnitBasis = qty > 0 ? +(lotTotalBasis / qty).toFixed(2) : unitFMV;
      const lotFMVTotal = +(unitFMV * qty).toFixed(2);
      if (inc.AsType === "Sealed") {
        const newId = nextId("S", "sealed", "Lot ID");
        DB.add("sealed", {
          "Lot ID": newId, "Game": inc.Game || "Pokemon",
          "Product": inc.Item || "", "Set": normalizeSetName(inc.Set), "Product Type": inc.ProductType || "",
          "Inventory Class": "Long Term Hold", "Qty": qty,
          "Acq Date": date, "Acq Type": "Trade", "Valuation Source": "Manual",
          "Unit Basis": lotUnitBasis, "Total Basis": lotTotalBasis,
          "Current FMV": lotFMVTotal, "Unrealized G/L": +(lotFMVTotal - lotTotalBasis).toFixed(2),
          "Source ID": tradeId,
          "Notes": `From trade ${tradeId}`,
        });
        inc["Lot ID"] = newId;
        effect.push({ kind: "lotCreate", table: "sealed", lotId: newId });
      } else {
        const newId = nextId("C", "cards", "Card Lot ID");
        DB.add("cards", {
          "Card Lot ID": newId, "Game": inc.Game || "Pokemon",
          "Card Name": inc.Item || "", "Set": normalizeSetName(inc.Set), "Card #": inc.Number || "",
          "Inventory Class": (inc.CardKind === "Graded" || looksGraded(String(inc.Condition || ""))) ? "Slab" : "Raw",
          "Condition/Grade": inc.Condition || (inc.CardKind === "Graded" ? "PSA 10" : "Near Mint"),
          "Qty": qty, "Acq Date": date, "Acq Type": "Trade",
          "Valuation Source": inc.Game === "Magic: The Gathering" ? "Scryfall" : "TCGPlayer",
          "Unit Basis": lotUnitBasis, "Total Basis": lotTotalBasis,
          "Current FMV": lotFMVTotal, "Unrealized G/L": +(lotFMVTotal - lotTotalBasis).toFixed(2),
          "Status": "Available", "Source ID": tradeId,
          "Image URL": inc.Image || "",
          "Notes": `From trade ${tradeId}`,
        });
        inc["Lot ID"] = newId;
        effect.push({ kind: "lotCreate", table: "cards", lotId: newId });
      }
    }
  } else if (isAccepted) {
    // Non-inventory edit on an Accepted trade - preserve its footprint.
    effect = effectForTransaction("trades", existing);
  } else {
    // Non-accepted (proposal / declined / cancelled): no inventory footprint.
    effect = [];
  }

  const tradeRow = {
    "Trade ID": tradeId, "Date": date,
    "Status":   status,
    "Outgoing": outgoing, "Incoming": incoming,
    "Outgoing Basis": +outBasis.toFixed(2),
    "Outgoing FMV":   +outFMV.toFixed(2),
    "Incoming FMV":   +inFMV.toFixed(2),
    "Embedded Gain/Loss": +(inFMV - outFMV + cash).toFixed(2),
    // Sum of per-item realized gains across outgoing - booked as taxable
    // disposition at the time of the trade.
    "Realized Gain":  totalRealizedGain,
    "Cash":           +cash.toFixed(2),
    // Reversible inventory footprint for edit/delete.
    "Inv Effect":     effect,
    "Notes": notes,
  };
  if (origId) DB.update("trades", "Trade ID", origId, tradeRow);
  else        DB.add("trades", tradeRow);

  // Optional embedded mileage trip — links to this trade via Related Deal/Lot.
  // (DOM reads happen synchronously, before closeModal tears the form down.)
  void saveMileageSection(tradeId, date);

  // eBay safety net: end live listings on card lots this trade sent out.
  {
    const depleted = effect
      .filter((e: any) => e.kind === "lotDelta" && (e.table === "cards" || e.table === "sealed") && (Number(e.dQty) || 0) < 0)
      .map((e: any) => String(e.lotId));
    if (depleted.length) void endEbayListingsForLots(depleted, `traded away (${tradeId})`);
  }

  delete (window as any)._tradeState;
  if (!isAccepted) {
    const statusDisp = status === "Proposed" ? "Pending" : status;
    toast(`${statusDisp} trade ${tradeId} saved (no inventory impact)`);
  } else if (isNew || !wasAccepted) {
    const parts = [`${outgoing.length} out / ${incoming.length} in`];
    if (bulkOutCards > 0 || bulkInCards > 0) {
      const bits: string[] = [];
      if (bulkOutCards > 0) bits.push(`-${fmtN(bulkOutCards)}`);
      if (bulkInCards > 0)  bits.push(`+${fmtN(bulkInCards)}`);
      parts.push(`bulk ${bits.join(" / ")} across ${bulkPoolsTouched.size} pool(s)`);
    }
    toast(`Trade ${tradeId} ${isNew ? "logged" : "completed"} · ${parts.join(" · ")} (inventory updated)`);
  } else {
    toast("Trade updated");
  }
  // Incoming trade items (incl. CSV imports) may lack catalog identity.
  requestEnrichment();
  closeModal();
  render();
}

/* ----- Outgoing add-search at the bottom of the items list ----- */
function _addTradeOutgoingFromLot(lot: LotOption): void {
  const s = S(); if (!s) return;
  const itemName = (lot.label.split(" — ")[0] || lot.id).replace(/^[A-Z]+-\d+-\d+ · /, "");
  s.Outgoing.push({
    "Lot ID": lot.id,
    "Item":   itemName,
    "Type":   lot.type,
    "Qty":    1,
    "Basis":  lot.basis,
    "FMV":    lot.fmv,
  });
  renderTradeOutgoingList();
  markModalDirty();
  // Scroll the items container so the freshly-added row is visible.
  setTimeout(() => {
    const c = document.getElementById("tradeOutgoing");
    if (c) c.scrollTop = c.scrollHeight;
  }, 10);
}

function _addTradeOutgoingFreeText(text: string): void {
  const s = S(); if (!s) return;
  s.Outgoing.push({ "Lot ID": "", "Item": text, "Type": "", "Qty": 1, "Basis": 0, "FMV": 0 });
  renderTradeOutgoingList();
  markModalDirty();
}

function wireTradeOutgoingAddSearch(): void {
  const wrapper = document.querySelector<HTMLElement>("[data-out-add-search]");
  if (!wrapper) return;
  const inp = wrapper.querySelector<HTMLInputElement>("input");
  if (!inp) return;
  wireLotSearch(wrapper, {
    excludeLocked: true,
    excludeIds: () => {
      const set = new Set<string>();
      const cur = S();
      (cur?.Outgoing || []).forEach(x => { if (x["Lot ID"]) set.add(x["Lot ID"]!); });
      return set;
    },
    onSelect: (lot) => {
      _addTradeOutgoingFromLot(lot);
      inp.value = "";
      inp.focus();
    },
  });
  // Free-text fallback: Enter with no dropdown selection adds a free-text row.
  inp.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || e.defaultPrevented) return;
    const list = wrapper.querySelector<HTMLElement>(".combobox-list");
    const hasActive = list?.querySelector(".combobox-item.active");
    const text = inp.value.trim();
    if (hasActive || !text) return;
    e.preventDefault();
    _addTradeOutgoingFreeText(text);
    inp.value = "";
  });
}

/* ----- Status chip next to Save button ----- */
function _updateStatusChip(): void {
  const sel = document.querySelector<HTMLSelectElement>('#tradeForm [name="Status"]');
  const chip = document.getElementById("tradeStatusChip");
  if (!sel || !chip) return;
  const status = sel.value || "Accepted";
  const cls = status === "Proposed"  ? "pill pill-amber"
            : status === "Declined"  ? "pill pill-red"
            : status === "Cancelled" ? "pill pill-gray"
            : "pill pill-blue";
  const label = status === "Proposed" ? "Pending"
              : status === "Accepted" ? "Completed - applies inventory"
              : status;
  chip.className = cls + " text-xs whitespace-nowrap";
  chip.textContent = label;
}

/** Called from editTrade after the modal mounts: paints the initial chip and
 *  wires it to the Status select so changes are reflected without a re-render. */
function initTradeStatusChip(): void {
  _updateStatusChip();
  const sel = document.querySelector<HTMLSelectElement>('#tradeForm [name="Status"]');
  sel?.addEventListener("change", _updateStatusChip);
}

/* ----- Print proposal from current form state (works on unsaved trades) ----- */
/** Build a trade-shaped object from the current form + _tradeState and hand it
 *  to the printable-proposal renderer. Lets the user print a proposal before
 *  the trade has been saved (important for the propose-before-committing flow). */
export function exportTradeProposalFromForm(): void {
  const cur = S(); if (!cur) return;
  const form = $("#tradeForm") as HTMLFormElement | null;
  const formData = form ? readForm(form) : {};
  const trade = {
    "Trade ID": String(formData["Trade ID"] || cur["Trade ID"] || "(unsaved)"),
    "Date":     String(formData["Date"] || cur["Date"] || todayISO()),
    "Status":   String(formData["Status"] || cur["Status"] || "Proposed"),
    "Notes":    String(formData["Notes"] || cur["Notes"] || ""),
    "Cash":     Number(formData["Cash"]) || Number(cur["Cash"]) || 0,
    "Outgoing": (cur.Outgoing || []).map(x => ({ ...x })),
    "Incoming": (cur.Incoming || []).map(x => ({ ...x })),
  };
  exportTradeProposal(trade);
}

// Inline-handler bridges
export async function acceptProposal(id: string): Promise<void> {
  await editTrade(id);
  // Pre-set Status to Accepted and Date to today after the modal mounts. The
  // user reviews and clicks Save to actually apply inventory + book realized
  // gain. saveTrade's date-auto-update logic also catches this if they don't
  // change anything (the displayed value matches the stored proposal date).
  setTimeout(() => {
    const form = document.querySelector("#tradeForm");
    if (!form) return;
    const sel = form.querySelector('[name="Status"]') as HTMLSelectElement | null;
    const dateInp = form.querySelector('[name="Date"]') as HTMLInputElement | null;
    const today = new Date().toISOString().slice(0, 10);
    if (sel) sel.value = "Accepted";
    if (dateInp) dateInp.value = today;
  }, 50);
}

/** One-step Accept: ask for confirmation, then commit the trade through the
 *  existing saveTrade pipeline. The form is mounted invisibly so saveTrade
 *  can read its values without the user ever seeing a modal flash. */
export async function quickAcceptProposal(id: string): Promise<void> {
  if (!confirm(
    `Complete trade ${id}?\n\n` +
    `This will decrement outgoing lots from inventory, create the incoming lots, ` +
    `book the realized gain on the outgoing side, and capitalize any cash boot ` +
    `into the incoming lots' basis. Date will be set to today.`
  )) return;

  const modalRoot = document.getElementById("modalRoot");
  if (modalRoot) modalRoot.style.visibility = "hidden";
  try {
    await editTrade(id);
    const form = document.querySelector("#tradeForm") as HTMLFormElement | null;
    if (!form) {
      toast("Couldn't load trade to accept.", "err");
      return;
    }
    const statusSel = form.querySelector('[name="Status"]') as HTMLSelectElement | null;
    const dateInp = form.querySelector('[name="Date"]') as HTMLInputElement | null;
    if (statusSel) statusSel.value = "Accepted";
    if (dateInp) dateInp.value = new Date().toISOString().slice(0, 10);
    saveTrade(id);
  } finally {
    if (modalRoot) modalRoot.style.visibility = "";
  }
}

(window as any).editTrade                     = editTrade;
(window as any).proposeTrade                  = () => editTrade(undefined, { seedStatus: "Proposed" });
(window as any).renderTradeIncomingList       = renderTradeIncomingList;
(window as any).renderTradeOutgoingList       = renderTradeOutgoingList;
(window as any).acceptProposal                = acceptProposal;
/** Save the current trade form as a Pending draft - sets Status to Proposed
 *  (UI label "Pending") and routes through saveTrade so the normal
 *  inventory-skipped path runs. */
export function saveTradeAsDraft(origId: string): void {
  const sel = document.querySelector<HTMLSelectElement>('#tradeForm [name="Status"]');
  if (sel) sel.value = "Proposed";
  saveTrade(origId);
}
(window as any).saveTradeAsDraft              = saveTradeAsDraft;
(window as any).quickAcceptProposal           = quickAcceptProposal;
(window as any).saveTrade                     = saveTrade;
(window as any).addTradeOutgoing              = addTradeOutgoing;
(window as any).addTradeOutgoingBulk          = addTradeOutgoingBulk;
(window as any).removeTradeOutgoing           = removeTradeOutgoing;
(window as any).updateTradeOutgoingField      = updateTradeOutgoingField;
(window as any).updateTradeOutgoingBulkClass  = updateTradeOutgoingBulkClass;
(window as any).addTradeIncoming              = addTradeIncoming;
(window as any).addTradeIncomingBulk          = addTradeIncomingBulk;
(window as any).lookupSealedForTradeIncoming  = lookupSealedForTradeIncoming;
(window as any).removeTradeIncoming           = removeTradeIncoming;
(window as any).updateTradeIncomingField      = updateTradeIncomingField;
(window as any).updateTradeIncomingBulkClass  = updateTradeIncomingBulkClass;
(window as any).lookupForTradeIncoming        = lookupForTradeIncoming;
(window as any).renderTradeIncomingList       = renderTradeIncomingList;
(window as any).updateTradeCash               = updateTradeCash;
(window as any).setTradeCashDirection         = setTradeCashDirection;
(window as any).updateTradeCashAmount         = updateTradeCashAmount;
(window as any).exportTradeProposalFromForm   = exportTradeProposalFromForm;
(window as any).updateTradeIncomingCardKind   = updateTradeIncomingCardKind;
